import { MailOutlined } from '@ant-design/icons'
import { type Carer, type Home, UserRole } from '@lmc/ts-common'
import { Avatar, Heading, Icon, IconType, Popover } from '@lmc/ui-kit'
import { Text } from '@lmc/ui-kit/src/general'
import { Button } from 'antd'
import type React from 'react'
import { useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'
import { commonPatterns } from '../../../../../../src/utils/enhancedTestIds'
import { getCarerRole } from '../utils'

type TeamListItemGroupsHeaderProps = {
  carer: Carer
  onClick: React.MouseEventHandler<HTMLDivElement>
  firstItem?: boolean
  homes: Home[]
  onDelete: (carer: Carer) => void
  testId?: string
}

const StyledContainer = styled.div<{ $firstItem?: boolean }>`
  background-color: ${(props) => props.theme.palette.white} !important;
  border-radius: 8px;
  margin-bottom: 10px;
  margin-top: ${(props) => (props?.$firstItem ? '24px' : '0px')};
  height: 64px;
  display: flex;
  gap: 10px;
  align-items: center;
  padding: 16px 12px;
  max-width: 1080px;
  margin-left: auto;
  margin-right: auto;
  width: 100%;
  cursor: pointer;
  &:hover {
    background-color: ${(props) =>
      props.theme.color.neutral.color10} !important;
  }
  ${({ theme }) => theme.mediaQueries.mobileDown} {
    margin-top: 0px;
  }
`

const StyledRows = styled.div`
  display: flex;
  flex-direction: column;
`

const StyledRow = styled.div`
  display: flex;
  align-items: center;
  height: 22px;
  gap: 10px;
`

const StyledPhone = styled(Icon)`
  color: ${(props) => props.theme.color.neutral.color5} !important;
`
const IconWrapper = styled.div`
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;

  &:hover {
    color: ${(props) => props.theme.color.hover.primary};
  }
  color: ${(props) => props.theme.color.neutral.color5} !important;
`
const StyledPaddingWrapper = styled.div`
  padding-left: 24px;
  padding-right: 24px;
`

const GrayText = styled(Text)`
  color: ${(props) => props.theme.color.neutral.color5};
  font-size: 12px;
`

const StyledText = styled(Text)`
  font-size: 12px;
`

const StyledHeading = styled(Heading)`
  font-size: 14px;
`

const ActionButton = styled(Button)`
  margin-left: auto;
  width: 42px;
  height: 42px;
`

const TeamListItemGroups: React.FC<TeamListItemGroupsHeaderProps> = ({
  carer,
  onClick,
  firstItem,
  homes,
  onDelete,
  testId
}) => {
  const picture = carer?.picture as unknown as string
  const name = `${carer?.name?.first} ${carer?.name?.last}`

  // Homes map
  const homesMap = useMemo(() => {
    return homes.reduce(
      (acc, next) => {
        acc[next.id] = next.name
        return acc
      },
      {} as Record<string, string>
    )
  }, [homes])

  // Sites names for carer (homes, home, homeGroup)
  const sites = useMemo(() => {
    const allSites = [
      ...(carer?.homes?.length
        ? carer.homes.map((id) => homesMap?.[id] || null).filter(Boolean)
        : []),
      carer.home ? homesMap?.[carer.home] || carer.home : []
    ].flat()

    // Remove duplicates by `name`
    const uniqueSites = Array.from(
      new Map(
        allSites.map((site) => [
          typeof site === 'object' ? site.name : site,
          site
        ])
      ).values()
    )

    return uniqueSites
  }, [carer.homes, carer.homeGroup, homesMap])

  const { t } = useTranslation()

  const handleDelete = useCallback(
    (event: React.MouseEvent<HTMLElement, MouseEvent>) => {
      event.stopPropagation()
      onDelete(carer)
    },
    [onDelete, carer]
  )

  return (
    <StyledPaddingWrapper>
      <StyledContainer
        onClick={onClick}
        $firstItem={firstItem}
        data-testid={testId}
      >
        <Avatar
          url={picture}
          size={40}
          data-testid={testId ? `${testId}-avatar` : undefined}
        />
        <StyledRows data-testid={testId ? `${testId}-content` : undefined}>
          <StyledRow data-testid={testId ? `${testId}-main-row` : undefined}>
            <StyledHeading
              level={5}
              data-testid={testId ? `${testId}-name` : undefined}
            >
              {name}
            </StyledHeading>
            {carer?.email && (
              <Popover
                content={
                  <Text>
                    <a href={`mailto:${carer.email}`}>{carer.email}</a>
                  </Text>
                }
              >
                <IconWrapper
                  data-testid={testId ? `${testId}-email` : undefined}
                >
                  <MailOutlined style={{ fontSize: '20px' }} />
                </IconWrapper>
              </Popover>
            )}
            {carer?.phoneNumber && (
              <Popover
                content={
                  <Text>
                    <a href={`tel:${carer.phoneNumber}`}>{carer.phoneNumber}</a>
                  </Text>
                }
              >
                <IconWrapper
                  data-testid={testId ? `${testId}-phone` : undefined}
                >
                  <StyledPhone customIcon={IconType.Phone} />
                </IconWrapper>
              </Popover>
            )}
          </StyledRow>
          <StyledRow data-testid={testId ? `${testId}-details-row` : undefined}>
            <StyledText
              data-testid={testId ? `${testId}-role-sites` : undefined}
            >
              {getCarerRole(carer?.role)} {sites.length > 0 && t('AT')}{' '}
              {sites.slice(0, 3).join(', ')}{' '}
              {sites.length > 3 && (
                <GrayText>{t('AND_N_MORE', { n: sites.length - 3 })}</GrayText>
              )}
            </StyledText>
          </StyledRow>
        </StyledRows>
        <ActionButton
          type="text"
          shape="round"
          icon={<Icon customIcon={IconType.Delete} />}
          onClick={handleDelete}
          data-testid={
            testId ? commonPatterns.button(testId, 'delete') : undefined
          }
        />
      </StyledContainer>
    </StyledPaddingWrapper>
  )
}

export default TeamListItemGroups
